import axios from "axios";
import React, { useEffect, useState } from "react";
import { FaBook } from "react-icons/fa";
import { Config } from "../../API/config";
import { useCookies } from "react-cookie";

interface Trainer {
    _id: string;
    name: string;
    email: string;
    phone: string;
    gender: string;
    profileImage: string;
    governorate: string;
    city: string;
    role: string;
    approvedAsTrainee: string;
    approvedAsTrainer: string;
    trainerId: string;
    createdAt: string;
    updatedAt: string;
}

interface Enrollment {
    _id: string;
    trainerId: Trainer;
    traineeId: string;
    status: "pending" | "opened" | "closed" | "completed"; // Added "completed" to status
    rate: number;
    price: number;
    createdAt: string;
    updatedAt: string;
}

const EnrolledCourses = () => {
    const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null); // New state for error messages
    const [cookies] = useCookies(["user-data", "token"]);
    const user = cookies["user-data"];

    const fetchEnrollmentData = async () => {
        try {
            setLoading(true);
            setError(null); // Reset error state before fetching
            const { data } = await axios.get(Config.getAllEnrollmentsForATrainee, {
                withCredentials: true,
            });

            if (data.statusCode === 200) {
                setEnrollments(data.data);
            } else {
                throw new Error("فشل جلب البيانات من الخادم"); // Handle non-200 status codes
            }
        } catch (err) {
            console.error("Error fetching enrollment data:", err);

            if (axios.isAxiosError(err)) {
                // Handle Axios-specific errors
                if (err.response) {
                    // Server responded with an error status (e.g., 400, 401, 403, 500)
                    const { status, data } = err.response;
                    if (status === 401) {
                        setError("غير مصرح لك بعرض هذه البيانات. الرجاء تسجيل الدخول مرة أخرى.");
                    } else if (status === 403) {
                        setError("ليس لديك الصلاحية للوصول إلى هذه البيانات.");
                    } else if (status === 404) {
                        setError("لم يتم العثور على بيانات التسجيل.");
                    } else {
                        setError(data.message || "حدث خطأ أثناء جلب البيانات من الخادم.");
                    }
                } else if (err.request) {
                    // Request was made, but no response (e.g., network error)
                    setError("فشل الاتصال بالخادم. تحقق من اتصالك بالإنترنت.");
                } else {
                    // Other errors (e.g., configuration issues)
                    setError("حدث خطأ غير متوقع أثناء جلب البيانات.");
                }
            } else {
                // Non-Axios errors (e.g., JavaScript runtime errors)
                setError("حدث خطأ غير متوقع.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnrollmentData();
    }, []);

    return (
        <div className="p-6 bg-white shadow-md rounded-lg" dir="rtl">
            <div className="flex flex-row items-center mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-md">
                    <FaBook size={18} className="text-primary-200" />
                </div>
                <h2 className="mr-3 text-[20px] font-[400]">الدورات المسجلة</h2>
            </div>

            {loading ? (
                <p className="text-center text-gray-500">جارٍ التحميل...</p>
            ) : error ? (
                <p className="text-center text-red-500">{error}</p> // Display error message
            ) : enrollments.length === 0 ? (
                <p className="text-center text-gray-500">لا توجد دورات مسجلة.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200 text-right text-sm md:text-base">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-2 md:p-3 border border-gray-200">حالة الدورة</th>
                                <th className="p-2 md:p-3 border border-gray-200">المدرب</th>
                                <th className="p-2 md:p-3 border border-gray-200">تاريخ التسجيل</th>
                            </tr>
                        </thead>
                        <tbody>
                            {enrollments.map((enrollment) => (
                                <tr key={enrollment._id} className="border border-gray-200">
                                    <td className="p-3">
                                        <span
                                            className={`px-3 py-1 rounded-full text-sm font-semibold ${enrollment.status === "completed"
                                                    ? "bg-green-200 text-white"
                                                    : enrollment.status === "closed"
                                                        ? "bg-red-200 text-white"
                                                        : enrollment.status === "opened"
                                                            ? "bg-yellow-200 text-white"
                                                            : "bg-blue-200 text-blue-800" // Default for "pending"
                                                }`}
                                        >
                                            {enrollment.status === "completed"
                                                ? "مكتملة"
                                                : enrollment.status === "closed"
                                                    ? "مغلقة"
                                                    : enrollment.status === "opened"
                                                        ? "مفتوحة"
                                                        : "قيد الانتظار" // Default for "pending"
                                            }
                                        </span>
                                    </td>
                                    <td className="p-2 md:p-3 whitespace-nowrap">{enrollment.trainerId.name}</td>
                                    <td className="p-2 md:p-3 whitespace-nowrap">
                                        {new Date(enrollment.createdAt).toLocaleDateString("ar-EG", {
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                        })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default EnrolledCourses;